import dayjs from "dayjs";
import { useCallback, useState } from "react";
import {
  useMessageInputHandlers,
  useMessageInputState,
} from "../../../App/State/Hooks/MessageDialog/useMessageInput";
import {
  useMessageScheduleHandlers,
  useMessageScheduleState,
} from "../../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { IMessageScheduleQueue } from "../../../App/Typings/MessageText";
import { SendMessagesMutationState } from "./useMessageMutationState";
import { ISelectedCandidate } from "../../../App/State/Slices/MessageDialogSlice";

export type MessageDialogState = ReturnType<typeof useMessageSendState>;

export default function useMessageSendState(
  messageMutationState: SendMessagesMutationState,
  selectedCandidates?: ISelectedCandidate[],
) {
  const [isScheduled, setIsScheduled] = useState<boolean>(false);

  const { body, subject, bodyErrorMessage, subjectErrorMessage } = useMessageInputState();
  const { handleClearInputState, handleValidateMessageInput, handleInputBodyChange } =
    useMessageInputHandlers();

  const { msgListQueue, scheduleDateTimeErrorMessage } = useMessageScheduleState();
  const { handleValidateScheduleInput } = useMessageScheduleHandlers();

  const {
    handleSendMessages,
    handleScheduleMessages,
    postSendMessageIsLoading,
    upsertMessageTemplateLoading,
  } = messageMutationState;

  const isValidRecipients = !selectedCandidates || selectedCandidates.length > 0;
  const isValidMessage = handleValidateMessageInput();
  const isValidScheduleState = handleValidateScheduleInput();

  const mutationLoading = postSendMessageIsLoading || upsertMessageTemplateLoading;
  const disableSaveMessageAction = mutationLoading || !isValidRecipients || !isValidMessage;
  const disableSaveMessageMessage = mutationLoading
    ? "Saving message..."
    : !isValidRecipients
      ? "No valid recipients selected"
      : !isValidMessage
        ? "Invalid message"
        : "";
  const disableScheduleAction = mutationLoading || !isValidRecipients || !msgListQueue.length;
  const disableAddToQueueAction =
    mutationLoading || !isValidRecipients || !isValidMessage || !isValidScheduleState;

  const disableSendMessageAction = postSendMessageIsLoading || !isValidMessage;

  let scheduleMessagesTooltipText = "Schedule Messages";
  if (postSendMessageIsLoading) {
    scheduleMessagesTooltipText = "Scheduling messages...";
  } else if (!isValidRecipients) {
    scheduleMessagesTooltipText = "No valid recipients selected";
  } else if (!msgListQueue.length) {
    scheduleMessagesTooltipText = "No Messages to Send";
  }

  let disabledScheduleMessagesTooltipText = "Cannot schedule messages";
  if (!isValidRecipients) {
    disabledScheduleMessagesTooltipText = "No valid recipients selected";
  } else if (!msgListQueue.length) {
    disabledScheduleMessagesTooltipText = "No Messages to Send";
  }

  let addButtonTooltipText = "Add Message to List (Ctrl+Enter)";
  if (!isValidRecipients) {
    addButtonTooltipText = "No valid recipients selected";
  } else if (bodyErrorMessage || subjectErrorMessage) {
    addButtonTooltipText = (bodyErrorMessage || subjectErrorMessage) as string;
  } else if (!isValidScheduleState && scheduleDateTimeErrorMessage) {
    addButtonTooltipText = scheduleDateTimeErrorMessage;
  } else if (body.length === 0) {
    addButtonTooltipText = "Invalid message";
  }

  const handleSendNow = useCallback(async () => {
    if (!disableSendMessageAction) {
      const now = dayjs().utc();
      const newMsg: IMessageScheduleQueue = {
        id: now.toISOString(),
        date: now,
        title: subject || "",
        bodyRaw: body,
        scheduled: false,
      };
      await handleSendMessages([newMsg]);
      handleClearInputState();
    }
  }, [disableSendMessageAction, handleClearInputState, handleSendMessages, body, subject]);

  return {
    getters: {
      isScheduled,
      disableSaveMessageAction,
      disableSaveMessageMessage,
      disableScheduleAction,
      disableAddToQueueAction,
      scheduleMessagesTooltipText,
      disabledScheduleMessagesTooltipText,
      addButtonTooltipText,
      postSendMessageIsLoading,
      upsertMessageTemplateLoading,
    },
    handlers: {
      setIsScheduled,
      handleSendNow,
      handleScheduleMessages,
      handleInputBodyChange,
    },
  };
}
